import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiLock, FiKey, FiShield } from 'react-icons/fi';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { changePassword, clearError } from '../redux/slices/authSlice';
import { toast } from 'react-toastify';
import './Settings.css';

const Settings = () => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());

    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })).unwrap();

      toast.success('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error || 'Failed to change password');
    }
  };

  return (
    <Layout title="Settings">
      <div className="settings-page">
        <div className="settings-grid">
          <Card title="Change Password" className="password-card">
            <div className="card-description">
              <FiShield className="description-icon" />
              <p>Ensure your account is using a strong password to stay secure.</p>
            </div>

            <form onSubmit={handleSubmit} className="password-form">
              <Input
                type="password"
                name="currentPassword"
                label="Current Password"
                value={passwordData.currentPassword}
                onChange={handleChange}
                placeholder="Enter your current password"
                error={errors.currentPassword}
                icon={<FiLock />}
              />

              <Input
                type="password"
                name="newPassword"
                label="New Password"
                value={passwordData.newPassword}
                onChange={handleChange}
                placeholder="Enter your new password"
                error={errors.newPassword}
                icon={<FiKey />}
              />

              <Input
                type="password"
                name="confirmPassword"
                label="Confirm New Password"
                value={passwordData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your new password"
                error={errors.confirmPassword}
                icon={<FiKey />}
              />

              <div className="password-requirements">
                <h4>Password Requirements:</h4>
                <ul>
                  <li className={passwordData.newPassword.length >= 8 ? 'valid' : ''}>
                    At least 8 characters
                  </li>
                  <li className={passwordData.newPassword !== passwordData.currentPassword && passwordData.newPassword ? 'valid' : ''}>
                    Different from current password
                  </li>
                  <li className={passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword ? 'valid' : ''}>
                    Passwords match
                  </li>
                </ul>
              </div>

              <div className="form-actions">
                <Button type="submit" loading={loading}>
                  Update Password
                </Button>
              </div>
            </form>
          </Card>

          <Card title="Security Information" className="security-card">
            <div className="security-tips">
              <h4>Security Tips</h4>
              <ul>
                <li>Use a unique password that you don't use elsewhere</li>
                <li>Include a mix of letters, numbers, and symbols</li>
                <li>Avoid using personal information like birthdays</li>
                <li>Consider using a password manager</li>
                <li>Change your password regularly</li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
